import type { InterviewInvite, InterviewOutcomeResult } from '~/types/interview-invite.types';
import type { InterviewInviteOutcome } from '~/enums/interview-invite.enums';

export const INTERVIEW_INVITE_FEATURE_OFF = Symbol('interview-invite-feature-off');
export const INTERVIEW_INVITE_GONE = Symbol('interview-invite-gone');
export type InterviewInviteResult = InterviewInvite | typeof INTERVIEW_INVITE_FEATURE_OFF;
export type InterviewOutcomeRecordResult = InterviewOutcomeResult | typeof INTERVIEW_INVITE_GONE;

export interface InterviewInviteApi {
  fetchInvite: () => Promise<InterviewInviteResult>;
  recordOutcome: (inviteId: number, outcome: InterviewInviteOutcome) => Promise<InterviewOutcomeRecordResult>;
}

function isNotFound (error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'statusCode' in error && error.statusCode === 404;
}

export function useInterviewInvite (): InterviewInviteApi {
  const fetchInvite = async (): Promise<InterviewInviteResult> => {
    try {
      return await useAuthenticatedFetchSimplified<InterviewInvite>(
        useApiUrl('/feedback-prompts/interview-invite'),
        { method: 'GET', retry: false },
      );
    } catch (error) {
      if (isNotFound(error)) {
        return INTERVIEW_INVITE_FEATURE_OFF;
      }
      throw error;
    }
  };

  const recordOutcome = async (inviteId: number, outcome: InterviewInviteOutcome): Promise<InterviewOutcomeRecordResult> => {
    try {
      return await useAuthenticatedFetchSimplified<InterviewOutcomeResult>(
        useApiUrl(`/feedback-prompts/interview-invite/${inviteId}/outcome`),
        { method: 'POST', body: { outcome }, retry: false },
      );
    } catch (error) {
      if (isNotFound(error)) {
        return INTERVIEW_INVITE_GONE;
      }
      throw error;
    }
  };

  return { fetchInvite, recordOutcome };
}
